import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  className?: string;
  zIndex?: number;
  showClose?: boolean;
  /**
   * When provided, the modal renders as an anchored dropdown positioned under
   * the trigger element on desktop (md+), and as a centered modal on mobile.
   * Replaces the old `SearchPopover` component.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Fixed width (px) for the anchored desktop dropdown. Defaults to 400. */
  anchorWidth?: number;
}

/**
 * Standardized Modal component for Mondivago.
 * Aligned with DESIGN.md and COMPONENTS.md.
 * Features glassmorphism backdrop and deep shadows.
 *
 * When `anchorRef` is set, the modal becomes a popover-style dropdown anchored
 * beneath the trigger on desktop and a centered modal on mobile.
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className = '',
  zIndex = 500,
  showClose = true,
  anchorRef,
  anchorWidth = 400,
}: ModalProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // useLayoutEffect sets position synchronously before the browser paints,
  // so the user never sees the flash at (0,0).
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.bottom + 8, left: rect.left + rect.width / 2 });
  }, [isOpen, anchorRef]);

  // Click outside to close — only for anchored (popover) mode.
  const desktopRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen || !anchorRef) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        desktopRef.current?.contains(target) ||
        mobileRef.current?.contains(target) ||
        anchorRef?.current?.contains(target)
      )
        return;
      onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, anchorRef, onClose]);

  // Only lock body scroll for full modals (no anchorRef) — anchored popovers are small dropdowns
  useEffect(() => {
    if (anchorRef) return;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, anchorRef]);

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-[95vw] h-[90vh]',
  };

  // ─── Anchored popover mode (desktop dropdown / mobile centered) ───
  if (anchorRef) {
    if (!isOpen) return null;
    return createPortal(
      <>
        {/* Mobile: centered modal */}
        <div
          className="fixed inset-0 z-[600] flex items-center justify-center bg-black/40 md:hidden"
          style={{ zIndex }}
        >
          <div
            ref={mobileRef}
            className="w-full max-w-[90%] rounded-[32px] bg-white p-6 shadow-2xl max-h-[85vh] overflow-y-auto"
          >
            <div className="mb-6 flex items-center justify-between">
              <h3 className="heading-3">{title}</h3>
              {showClose && (
                <Button
                  shape="icon"
                  icon={X}
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="!text-gray-400 hover:!text-gray-600"
                  aria-label="Close"
                />
              )}
            </div>
            {children}
          </div>
        </div>

        {/* Desktop: anchored dropdown via portal (escapes overflow:hidden) */}
        <div
          ref={desktopRef}
          className="fixed z-[600] hidden w-[400px] rounded-[32px] border border-gray-100 bg-white p-6 shadow-2xl md:block overflow-visible"
          style={{
            top: position.top,
            left: position.left,
            width: anchorWidth,
            transform: 'translateX(-50%)',
            zIndex,
          }}
        >
          <div className="mb-6">
            <h3 className="heading-3">{title}</h3>
          </div>
          {children}
        </div>
      </>,
      document.body
    );
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex }}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Container */}
      <div
        className={`relative w-full max-h-[85vh] overflow-visible rounded-2xl sm:rounded-[32px] border border-gray-200 bg-white p-4 sm:p-6 shadow-2xl transform flex flex-col ${sizeClasses[size]} ${className}`}
      >
        {/* Header */}
        {(title !== undefined || description !== undefined || showClose) && (
          <div className="mb-6 shrink-0">
            <div className="flex items-start justify-between gap-4">
              {title ? <h3 className="heading-3 leading-tight">{title}</h3> : <div />}
              {showClose && (
                <Button
                  shape="icon"
                  icon={X}
                  variant="outline"
                  size="sm"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-900 shrink-0"
                  aria-label="Close modal"
                />
              )}
            </div>
            {description && <p className="mt-2 desc-md leading-relaxed pr-8">{description}</p>}
          </div>
        )}

        {/* Content */}
        <div className="relative flex-1 min-h-0 overflow-y-auto max-h-[70vh] custom-scrollbar pr-1">{children}</div>

        {/* Footer */}
        {footer && <div className="mt-8 shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export interface ActionModalProps extends Omit<ModalProps, 'footer' | 'children'> {
  children?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  // Aliases for compatibility
  confirmLabel?: string;
  onConfirm?: () => void;
  actionVariant?: 'primary' | 'danger';
  variant?: 'primary' | 'danger';
  cancelLabel?: string;
  isLoading?: boolean;
  isActionDisabled?: boolean;
}

/**
 * Specialized Action Modal for confirm/cancel patterns.
 * Automatically implements the standard footer with Primary/Secondary buttons.
 */
export const ActionModal = ({
  actionLabel,
  onAction,
  confirmLabel,
  onConfirm,
  actionVariant = 'primary',
  variant,
  cancelLabel = 'Cancel',
  isLoading = false,
  isActionDisabled = false,
  children,
  ...props
}: ActionModalProps) => {
  const finalActionLabel = actionLabel ?? confirmLabel ?? 'Confirm';
  const finalOnAction =
    onAction ??
    onConfirm ??
    (() => {
      /* noop */
    });
  const finalVariant = variant ?? actionVariant;

  return (
    <Modal
      {...props}
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" onClick={props.onClose} fullWidth disabled={isLoading} title={cancelLabel} />
          <Button
            variant={finalVariant}
            onClick={finalOnAction}
            fullWidth
            isLoading={isLoading}
            disabled={isActionDisabled || isLoading}
            title={finalActionLabel}
          />
        </div>
      }
    >
      {children}
    </Modal>
  );
};
